import {
  type ListMode,
  type PrismaClient,
  type PrismaTXN,
  type UserRepoSummary,
  type CommitsByDay,
  type TeamContact,
} from "@openqlabs/drm-db";
export * as Rest from "./rest";
export * as Constants from "./constants";
export * as Logger from "./logger";

import {
  getFourWeeksAgoIso,
  getSixWeeksAgoIso,
  REPO_NOT_ADDED_REASONS,
} from "./constants";
export * as Types from "./types";
import { GitguruCommit } from "./graphql/types";
export * as GithubGraphql from "./graphql";
export * as Authentication from "./authenticationManager";
export * as KafkaConstants from "./kafkaConstants";
import * as Enums from "./enums";
export * as Enums from "./enums";
export { formatAxiosError, errorToString } from "./errorHandling";
import upsertListEvaluation from "./upsertListEvaluation";
import upsertOverviewEvaluation from "./upsertOverviewEvaluation";

export const defaultFileNames = [
  "Pipfile",
  "requirements.txt",
  "package.json",
  "go.mod",
  "pom.xml",
  "build.gradle",
  "Gemfile",
  "Cargo.toml",
  ".cabal",
  "composer.json",
  "hardhat.config",
  "truffle",
  "network",
  "deployments",
  "foundry.toml",
];

export const sourceKeyToLabelMap: Record<string, string> = {
  firstName: "Full Name",
  lastName: "Full Name",
  emailAddresses: "Email Addresses",
  description: "Description",
  company: "Company",
  jobTitle: "Job Title",
  phoneNumbers: "Phone Numbers",
  primaryLocation: "Primary Location",
  twitterUrl: "Twitter",
  instagramUrl: "Instagram",
  facebookUrl: "Facebook",
  linkedinUrl: "LinkedIn",
};

export const getAttributeLabel = (attribute: string) => {
  return sourceKeyToLabelMap[attribute] ?? attribute;
};

export const getUserName = (user: {
  firstName: string;
  lastName: string;
  name: string;
}) => {
  if (user.firstName || user.lastName)
    return `${user.firstName} ${user.lastName}`;
  return user.name;
};

export const formatTimeTable = (time: number | undefined | null) => {
  if (!time) return "N/A";
  const date = new Date(time);
  return `${date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "UTC",
  })}.${date.getUTCMilliseconds().toString().padStart(3, "0")}`;
};

export const formatDateTable = (time: number | undefined | null) => {
  if (!time) return "N/A";
  return new Date(time).toLocaleString();
};
export const getListStartDate = (
  list: {
    mode: ListMode | null;
    startDate: Date;
  } | null
) => {
  return list?.mode === Enums.ListMode.DEEP
    ? list?.startDate.toISOString()
    : list?.mode === Enums.ListMode.LIGHT
      ? getSixWeeksAgoIso()
      : getFourWeeksAgoIso();
};

export function filterUnwantedAuthors(commit: GitguruCommit) {
  return (
    !commit.author_email.includes("[bot]") &&
    commit.author_email !== "action@github.com" &&
    !commit.author.includes("[bot]") &&
    commit.github_graphql_id &&
    commit.github_graphql_id !== ""
  );
}

export function getCurrentTimestamp() {
  return new Date().toISOString();
}

export const getFirstDayOfMonthISO = (yearMonth: string): string => {
  const [year, month] = yearMonth.split("-");
  return new Date(`${year}-${month}-01`).toISOString();
};

export const getFirstDayOfNextMonthISO = (yearMonth: string): string => {
  const [year, month] = yearMonth.split("-");
  const nextMonth = parseInt(month) === 12 ? 1 : parseInt(month) + 1;
  const nextYear = nextMonth === 1 ? parseInt(year) + 1 : parseInt(year);
  return new Date(nextYear, nextMonth - 1, 1).toISOString();
};

export const createOrUpdateTeamEvals = async (
  prisma: PrismaClient | PrismaTXN,
  params: {
    teamAccountId: string;
    listId: string;
  }
) => {
  await upsertListEvaluation(prisma, params.listId, params.teamAccountId);
  await upsertOverviewEvaluation(prisma, params.teamAccountId);
};

export const encodeStatusAsInt = (
  status: Statuses,
  mode: ListMode | null
): number => {
  if (mode == Enums.ListMode.DEEP) {
    switch (status) {
      // status numbers for list in deep mode
      case "lead":
        return 1;
      case "new lead":
        return 2;
      case "customer":
        return 3;
      case "lead inactive":
        return 4;
      case "new lead inactive":
        return 5;
      case "customer inactive":
        return 6;
      case "lead churned":
        return 7;
      case "new lead churned":
        return 8;
      case "customer churned":
        return 9;
      case "inactive":
        return 10;
      case "churned":
        return 11;
      case "cold":
        return 12;
    }
  } else if (mode == Enums.ListMode.LIGHT) {
    // status numbers for list in light mode
    switch (status) {
      case "lead":
      case "new lead":
      case "customer":
        return 13;
      case "lead inactive":
      case "new lead inactive":
      case "customer inactive":
      case "inactive":
      case "cold":
        return 14;
      case "lead churned":
      case "new lead churned":
      case "customer churned":
      case "churned":
        return 15;
    }
  } else if (mode == Enums.ListMode.ULTRALIGHT) {
    // status numbers for list in ultra light mode
    switch (status) {
      case "lead":
      case "new lead":
      case "customer":
        return 13;
      case "lead inactive":
      case "new lead inactive":
      case "customer inactive":
      case "inactive":
      case "cold":
        return 14;
      case "lead churned":
      case "new lead churned":
      case "customer churned":
      case "churned":
        return 15;
    }
  } else {
    throw new Error(`UNKNOWN LIST MODE!: ${mode}`);
  }
};

export type Statuses =
  | "lead"
  | "new lead"
  | "customer"
  | "lead inactive"
  | "new lead inactive"
  | "customer inactive"
  | "lead churned"
  | "new lead churned"
  | "customer churned"
  | "inactive"
  | "churned"
  | "cold";

const getTooltip = (status: string, lightMode?: boolean) => {
  // note that lightMode only makes a difference for a user's status
  // Best definition we have of different statuses at the moment!
  if (!lightMode) {
    switch (status) {
      case "lead":
        return "Lead generated during list";
      case "new lead":
        return "Lead generated post list";
      case "lead inactive":
        return "New lead but inactive for 6 weeks";
      case "new lead inactive":
        return "Lead generated post list but inactive for 6 weeks";
      case "lead churned":
        return "New lead but dependency was removed";
      case "new lead churned":
        return "Lead generated post list but dependency was removed";
      case "customer":
        return "";
      case "customer inactive":
        return "Returning customer but inactive for 6 weeks";
      case "customer churned":
        return "Returning customer but dependency was removed";
      case "churned":
        return "Customer churned before list start";
      case "inactive":
        return "Customer inactive since list start";
      case "cold":
        return "Customer has never been active";

      default:
        return "No evaluation result available yet";
    }
  } else {
    switch (status) {
      // in Light mode we only analyze the last 6 weeks for user dependencies
      // hence we can only know if users are active/inactive/churned
      // NB: repo-dependencies can still be shown in detail aka deep mode

      case "active":
        return "User active over the past 6 weeks";
      case "inactive":
        return "User inactive over the past 6 weeks";
      case "churned":
        return "User churned over the past 6 weeks";

      default:
        return "No dependency evaluation result available yet";
    }
  }
};

// decode status as object with string and color and tooltip
export const decodeStatusAsObject = (
  status: number | null | undefined,
  manualStatus: number | null | undefined,
  engagement = false
) => {
  const manualStatusTooltip = manualStatus ? " (manual)" : "";
  switch (status) {
    case 1:
      return {
        status: "lead",
        color: "bg-new",
        tooltip: getTooltip("lead") + manualStatusTooltip,
      };
    case 2:
      return {
        status: "new lead",
        color: "bg-new",
        tooltip: getTooltip("new lead") + manualStatusTooltip,
      };
    case 3:
      return {
        status: "customer",
        color: "bg-customer",
        tooltip: getTooltip("customer") + manualStatusTooltip,
      };
    case 4:
      return {
        status: "lead 😴",
        color: "bg-new",
        tooltip: getTooltip("lead inactive") + manualStatusTooltip,
      };
    case 5:
      return {
        status: "new lead 😴",
        color: "bg-new",
        tooltip: getTooltip("new lead inactive") + manualStatusTooltip,
      };
    case 6:
      return {
        status: "customer 😴",
        color: "bg-customer",
        tooltip: getTooltip("customer inactive") + manualStatusTooltip,
      };
    case 7:
      return {
        status: "lead ❌",
        color: "bg-churned",
        tooltip: getTooltip("lead churned") + manualStatusTooltip,
      };
    case 8:
      return {
        status: "new lead ❌",
        color: "bg-churned",
        tooltip: getTooltip("new lead churned") + manualStatusTooltip,
      };
    case 9:
      return {
        status: "customer ❌",
        color: "bg-churned",
        tooltip: getTooltip("customer churned") + manualStatusTooltip,
      };
    case 10:
      return {
        status: "inactive",
        color: "bg-blue-200",
        tooltip: getTooltip("inactive") + manualStatusTooltip,
      };
    case 11:
      return {
        status: "churned",
        color: "bg-churned",
        tooltip: getTooltip("churned") + manualStatusTooltip,
      };
    case 12:
      return {
        status: "cold",
        color: "bg-cold",
        tooltip: getTooltip("cold") + manualStatusTooltip,
      };
    case 13:
      return {
        status: engagement ? "customer" : "active",
        color: "bg-customer",
        tooltip:
          getTooltip(engagement ? "customer" : "active", true) +
          manualStatusTooltip,
      };
    case 14:
      return {
        status: "inactive",
        color: "bg-blue-200",
        tooltip: getTooltip("inactive", true) + manualStatusTooltip,
      };
    case 15:
      return {
        status: "churned",
        color: "bg-churned",
        tooltip: getTooltip("churned", true) + manualStatusTooltip,
      };
    default:
      return {
        status: "no status",
        color: "bg-offwhite",
        tooltip: getTooltip("no status"),
      };
  }
};

export const pluralize = (word: string, count: number) => {
  if (count === 1) return word;
  const lastLetter = word[word.length - 1];
  if (lastLetter === "y") return word.slice(0, -1) + "ies";
  return word + "s";
};

export const readableErrorFromScreamingSnake = (error: string) => {
  return error
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export function kbToMb(kb: number) {
  return Math.floor(kb / 1000);
}

export const getGithubName = (url?: string) => {
  if (!url) return "";

  const result = url.replace(/^https:\/\/github\.com\//, "").replace(/\/$/, "");
  return result;
};

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const asyncForEach = async <T>(
  array: T[],
  callback: (item: T, index: number, array: T[]) => Promise<void>
) => {
  for (let index = 0; index < array.length; index++) {
    const item = array[index];
    await callback(item, index, array);
  }
};

export const refetchHandler = (
  data: { status: string }[] | undefined,
  length?: number
): number | false => {
  if (
    data?.some(
      (elem) => elem.status === "waiting" || (length && data.length < length)
    )
  ) {
    return 60 * 60 * 1000;
  }
  return false;
};

export const getReadableDate = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const getReadableDateTime = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
};

export const getMonthDistance = (endDate: Date, startDate?: Date) => {
  if (!startDate) return 0;
  const years = endDate.getFullYear() - startDate.getFullYear();
  const months = endDate.getMonth() - startDate.getMonth();
  let totalMonths = years * 12 + months;

  if (endDate.getDate() < startDate.getDate()) {
    totalMonths--;
  }

  return totalMonths;
};
export function nukeNegatives(val?: number | string | null) {
  if (!val) return 0;
  if (typeof val === "string") val = parseFloat(val);
  if (val < 0) return 0;
  if (Number.isNaN(val)) return 0;
  else return Math.round(val);
}

export const maxOne = (score: number | null) => {
  if (score === null) return 0;
  if (score > 1) return 1;
  return score;
};

export const getMonthFromNumber = (monthNumber: number) => {
  switch (monthNumber) {
    case 0:
      return "January";
    case 1:
      return "February";
    case 2:
      return "March";
    case 3:
      return "April";
    case 4:
      return "May";
    case 5:
      return "June";
    case 6:
      return "July";
    case 7:
      return "August";
    case 8:
      return "September";
    case 9:
      return "October";
    case 10:
      return "November";
    case 11:
      return "December";
    default:
      return "January";
  }
};

export const getUniqueArray = <T extends { id: string }>(array: T[]): T[] =>
  array.reduce<T[]>((accumulator, current) => {
    const existingItem = accumulator.find((item) => item.id === current.id);
    if (!existingItem) {
      accumulator.push(current);
    }
    return accumulator;
  }, []);
export const getUniqueArraySimple = <T>(array: T[]): T[] =>
  array.reduce<T[]>((accumulator, current) => {
    const existingItem = accumulator.includes(current);
    if (!existingItem) {
      accumulator.push(current);
    }
    return accumulator;
  }, []);

export const getRank = (daysActive: number) => {
  switch (true) {
    case daysActive === 0:
      return "inactive";
    case daysActive === 1:
      return "one-time";
    case daysActive >= 10:
      return "full-time";
    default:
      return "part-time";
  }
};

export const makeLink = (link: string) => {
  return link.startsWith("http") ? link : `https://${link}`;
};

export const getTotalUsedCredits = async (
  teamAccountId: string,
  prisma: PrismaClient
) => {
  const contactCreditsConsumed = await prisma.teamAccount.findUnique({
    where: { id: teamAccountId },
    select: { contactCreditsConsumed: true },
  });

  return contactCreditsConsumed?.contactCreditsConsumed ?? 0;
};

export const getPersonOrCompanyString = (
  person: { type: string | null | undefined } | null | undefined
) => {
  if (person?.type === "user") {
    return "person";
  }
  return "company";
};

export const getDayOnlyIso = () => {
  return new Date().toISOString().slice(0, 10);
};

export const runTeamAccountCreditsCheck = async (
  teamAccountId: string,
  prisma: PrismaClient
) => {
  const totalUsedCredits = await getTotalUsedCredits(teamAccountId, prisma);

  const teamAccount = await prisma.teamAccount.findUnique({
    where: { id: teamAccountId },
    include: { stripeSubscription: true },
  });

  const totalContactsCredits =
    teamAccount?.stripeSubscription?.totalContacts ?? 0;

  if (totalContactsCredits <= totalUsedCredits) {
    throw new Error("Insufficient credits");
  }
};

export const isInRepoNotAddedReasons = (error: string): boolean => {
  return REPO_NOT_ADDED_REASONS.includes(error);
};

export function getAllMonths(userRepoSummary: UserRepoSummary[]): string[] {
  let oldestMonth = new Date();
  let newestMonth = new Date();
  userRepoSummary.forEach((repo) => {
    repo.userActivityWithUniqueMonth.forEach((monthlyActivity) => {
      if (new Date(monthlyActivity.month) < oldestMonth) {
        oldestMonth = new Date(monthlyActivity.month);
      }
      if (new Date(monthlyActivity.month) > newestMonth) {
        newestMonth = new Date(monthlyActivity.month);
      }
    });
  });

  const allMonthsInRange = [];
  const currentMonth = new Date(oldestMonth);

  while (currentMonth <= newestMonth) {
    allMonthsInRange.push(currentMonth.toISOString().slice(0, 7));
    currentMonth.setMonth(currentMonth.getMonth() + 1);
  }

  return allMonthsInRange;
}

export const getLatestCommitDate = (commitsByDay?: CommitsByDay) => {
  if (commitsByDay) {
    const sortedCommitsByDay = Object.keys(commitsByDay).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });
    const latestDateBasic = sortedCommitsByDay[sortedCommitsByDay.length - 1];

    return new Date(latestDateBasic).getTime();
  } else return 0;
};

export const getNameFromTeamContact = (teamContact: TeamContact) => {
  if (teamContact.firstName) {
    return `${teamContact.firstName} ${teamContact.lastName}`;
  }
  return teamContact.githubName;
};
